import { useState, useEffect, useCallback, useContext } from "react";
import "./users.scss";
import { useNavigate } from "react-router-dom";
import { notification, Modal, Space, Select } from "antd";
import axios from "axios";
import { z, ZodType } from "zod";
import { ExclamationCircleFilled } from "@ant-design/icons";

// context
import { AuthContext } from "../../../contexts/authContext";

// icon
import {
  addIcon,
  logoutIcon,
  visibleIcon,
  invisibleIcon,
} from "../../../constants/icons";

// ui-component
import Loaders from "../../../components/UI/Loaders/Loaders";
import EmptyData from "../../../components/UI/EmptyData/EmptyData";

// utils
import monitorInputChange from "../../../utils/monitorInputChange";

// interfaces
import { ErrorParams } from "../../../interfaces/errorParams";
interface UserData {
  _id: string;
  email: string;
  name: string;
  organization: string;
  role: string;
  last_logged_in: string;
}

interface UserSessionData {
  _id: string;
  email: string;
  name: string;
  organization: string;
  role: string;
  last_logged_in: string;
  token: string;
  token_expiry: string;
}

interface AddUserModalData {
  modal: boolean;
  form: {
    new_name: string;
    new_email: string;
    new_password: string;
    new_organization: string;
  };
}

type NotificationType = "success" | "info" | "warning" | "error";

const API_URL = "/api/v1";
const { confirm } = Modal;
const Users = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [api, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<UserData[]>([]);
  const [userDetailModal, setUserDetailModal] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<UserSessionData>({
    _id: "",
    name: "",
    email: "",
    organization: "",
    role: "",
    last_logged_in: "",
    token: "",
    token_expiry: "",
  });
  const [updating, setUpdating] = useState<boolean>(false);
  const [newUser, setNewUser] = useState<AddUserModalData>({
    modal: false,
    form: {
      new_name: "",
      new_email: "",
      new_password: "",
      new_organization: "",
    },
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errorParams, setErrorParams] = useState<ErrorParams[]>([]);

  const formSchema: ZodType<{
    new_name: string;
    new_email: string;
    new_password: string;
    new_organization: string;
  }> = z.object({
    new_name: z.string().min(2),
    new_email: z.string().email(),
    new_password: z.string().min(6),
    new_organization: z.string().min(1).max(150),
  });

  const opNotification = (
    type: NotificationType,
    message: string,
    descripton: string
  ) => {
    api[type]({
      message: message,
      description: descripton,
    });
  };

  const resetNewUserForm = () => {
    setNewUser({
      modal: false,
      form: {
        new_name: "",
        new_email: "",
        new_password: "",
        new_organization: "",
      },
    });
  };

  const fetchUserRole = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/role/${user.id}`);
      if (res.status === 200 && res.data.success === true) {
        let role = res.data.role;
        return role;
      } else {
        return "user";
      }
    } catch (error: any) {
      console.log("Error fetching user role", error);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const role = await fetchUserRole();
      if (user && role === "admin") {
        const res = await axios.get(`${API_URL}/users/list`);
        if (res.status === 200 && res.data.success === true) {
          setUsers(res.data.users);
        }
      } else {
        navigate("/dashboard/evaluation");
      }
    } catch (error: any) {
      if (
        error.response.status === 403 &&
        error.response.data.access === false
      ) {
        navigate("/dashboard/evaluation");
      }
      if (error.response) {
        const { status } = error.response;
        if (status === 401 || status === 500) {
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  const fetchUserInfo = async (data: UserData) => {
    try {
      const res = await axios.get(`${API_URL}/users/info/${data._id}`);
      if (res.status === 200) {
        if (res.data.success === false) {
          setUserInfo({
            ...data,
            token: "None",
            token_expiry: "Not Data",
          });
        } else {
          setUserInfo({
            ...data,
            token: res.data.session.token,
            token_expiry: res.data.session.token_expiry,
          });
        }
      }
    } catch (error: any) {
      console.log("Error fetcing user log data", error);
      if (error.response) {
        const { status } = error.response;
        if (status === 401 || status === 500) {
          setUser(null);
        }
      }
    }
  };

  const handleUpdateUserDetail = async () => {
    try {
      setUpdating(true);
      const res = await axios.put(`${API_URL}/users/update/role`, {
        id: userInfo._id,
        role: userInfo.role,
      });
      if (res.status === 200 && res.data.success === true) {
        setUsers(
          users.map((usr) => {
            if (usr._id.trim() === userInfo._id.trim()) {
              return {
                ...usr,
                role: userInfo.role,
              };
            } else {
              return usr;
            }
          })
        );
        setUserInfo({
          _id: "",
          name: "",
          email: "",
          organization: "",
          role: "",
          last_logged_in: "",
          token: "",
          token_expiry: "",
        });
        setUserDetailModal(false);
        opNotification(
          "success",
          "Account Update",
          "User account information updated successfully."
        );
      }
    } catch (error: any) {
      console.log("Error updating user detail", error);
      if (error.response) {
        const { status } = error.response;
        if (status === 401 || status === 500) {
          setUser(null);
        }
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleLogoutUser = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/users/logout/user/${userInfo._id}`
      );
      if (res.status === 200 && res.data.success === true) {
        setUsers(
          users.map((usr) => {
            if (usr._id.trim() === userInfo._id.trim()) {
              return {
                ...usr,
                token: "none",
              };
            } else {
              return usr;
            }
          })
        );
        setUserInfo({
          _id: "",
          name: "",
          email: "",
          organization: "",
          role: "",
          last_logged_in: "",
          token: "",
          token_expiry: "",
        });
        setUserDetailModal(false);
        opNotification(
          "success",
          "Terminate Session",
          "User session terminated."
        );
      }
    } catch (error: any) {
      console.log("Error lgging out user", error);
      if (error.response) {
        const { status } = error.response;
        if (status === 401 || status === 500) {
          setUser(null);
        }
        if (status === 400) {
          opNotification(
            "info",
            "Terminate Session",
            "User session does not exists!"
          );
        }
      }
    }
  };

  const handleFormValidate = () => {
    const errorlist: any[] = [];
    const validation = formSchema.safeParse(newUser.form);
    if (!validation.success) {
      const { errors } = validation.error;
      errors.forEach((err) => {
        errorlist.push({
          path: err.path[0] as string,
          message: err.message,
        });
      });
    }
    setErrorParams(errorlist);
    if (errorlist.length > 0) {
      return false;
    }
    return true;
  };

  const handleCreateNewUser = async () => {
    try {
      const validate = handleFormValidate();
      if (!validate) {
        return false;
      }

      // format form data
      const formdata = {
        name: newUser.form.new_name,
        email: newUser.form.new_email,
        password: newUser.form.new_password,
        organisation: newUser.form.new_organization,
      };

      const res = await axios.post(`${API_URL}/auth/signup/`, formdata);
      if (res.status === 201 && res.data.success === true) {
        setUsers([res.data.userinfo, ...users]);
        resetNewUserForm();
        opNotification(
          "success",
          "New Account Created",
          "New user account created successfully!"
        );
      }
    } catch (error: any) {
      if (
        error.response.status === 400 &&
        error.response.data.success === false &&
        error.response.data.error === "email-exist"
      ) {
        opNotification("error", "Account Exists", "Account already exists!");
        return false;
      }
    }
  };

  const deleteAccount = async (id: string) => {
    try {
      const res = await axios.delete(`${API_URL}/users/delete/account/${id}`);
      if (res.status === 200 && res.data.success === true) {
        setUsers((prev) => prev.filter((usr) => usr._id.trim() !== id.trim()));
        setUserDetailModal(false)
        opNotification("success", "Account Deleted", "User account deleted!");
      }
    } catch (error) {
      console.log("Could not delete user account", error);
    }
  };

  const handleDeleteUserAccount = (id: string) => {
    confirm({
      title: "Delete User Account",
      icon: <ExclamationCircleFilled />,
      content: "Do you want to delete this user account?",
      centered: true,
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: {
        style: {
          backgroundColor: "#ff4d4f",
          color: "white",
        },
      },
      cancelButtonProps: {
        style: {
          backgroundColor: "white",
          color: "#1a1d21",
          borderColor: "#1a1d21",
        },
      },

      onCancel() {
        //
      },
      onOk() {
        deleteAccount(id);
      },
    });
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (errorParams.length > 0) {
      errorParams.forEach((err) => {
        const ele = document.getElementById(err.path);
        if (ele) {
          ele.style.borderColor = "#FF7875";
        }
      });
    }
  }, [errorParams]);

  return (
    <div className="users">
      {contextHolder}
      <div className="container">
        <div className="appbar">
          <span className="title">Users</span>
          <button
            onClick={() => {
              setNewUser({
                ...newUser,
                modal: true,
              });
            }}
          >
            {addIcon} Add User
          </button>
        </div>
        <div className="listContainer">
          {loading ? (
            <Loaders />
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <td className="index">#</td>
                    <td>Name</td>
                    <td>Email</td>
                    <td>Organization</td>
                    <td>Role</td>
                    <td>Last Logged In</td>
                  </tr>
                </thead>
                <tbody>
                  {users.map((usr: UserData, index: number) => (
                    <tr
                      key={usr._id}
                      onClick={() => {
                        setUserDetailModal(true);
                        fetchUserInfo(usr);
                      }}
                    >
                      <td className="index">{index + 1}</td>
                      <td>{usr.name}</td>
                      <td>{usr.email}</td>
                      <td>{usr.organization}</td>
                      <td>{usr.role === "admin" ? "Admin" : "User"}</td>
                      <td>{usr.last_logged_in}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && <EmptyData />}
            </>
          )}
        </div>
      </div>
      <Modal
        open={userDetailModal}
        centered
        onCancel={() => {
          setUserDetailModal(false);
        }}
        closable={false}
        footer={null}
      >
        <div className="userDetailModalContainer">
          <span className="header">User Information</span>
          <div className="userInfoModalContainer">
            <div className="infoHolder">
              <span>Name</span>
              <span className="staticInput">{userInfo.name}</span>
            </div>
            <div className="infoHolder">
              <span>Email</span>
              <span className="staticInput">{userInfo.email}</span>
            </div>
            <div className="infoHolder">
              <span>Organization</span>
              <span className="staticInput">{userInfo.organization}</span>
            </div>
            <div className="infoHolderRow">
              <div className="infoHolderColumn">
                <span>Role</span>
                <Space
                  style={{ width: "100%", height: 40 }}
                  direction="vertical"
                >
                  <Select
                    style={{
                      width: "100%",
                      height: 40,
                      whiteSpace: "nowrap",
                    }}
                    value={userInfo.role}
                    onChange={(value: string, data: any) => {
                      setUserInfo({
                        ...userInfo,
                        role: value,
                      });
                    }}
                    options={[
                      {
                        label: "User",
                        value: "user",
                      },
                      {
                        label: "Admin",
                        value: "admin",
                      },
                    ]}
                  />
                </Space>
              </div>
              <div className="infoHolderColumn">
                <span>Last logged in</span>
                <span className="staticInput">{userInfo.last_logged_in}</span>
              </div>
            </div>
            <div className="infoHolder">
              <span>Token</span>
              <span className="staticToken">{userInfo.token}</span>
            </div>
            <div className="infoHolderRow">
              <div className="infoHolderColumn">
                <span>Token Expiry</span>
                <span className="staticInput">{userInfo.token_expiry}</span>
              </div>
              <div className="infoHolderColumn">
                <span>Action</span>
                <button onClick={handleLogoutUser}>
                  {" "}
                  {logoutIcon} Terminate Session
                </button>
              </div>
            </div>
          </div>
          <div className="buttonHolder">
            <div className="delHolder">
              <button
                className="btnDelete"
                onClick={() => {
                  handleDeleteUserAccount(userInfo._id);
                }}
              >
                Delete
              </button>
            </div>
            <button
              onClick={() => {
                setUserInfo({
                  _id: "",
                  name: "",
                  email: "",
                  organization: "",
                  role: "",
                  last_logged_in: "",
                  token: "",
                  token_expiry: "",
                });
                setUserDetailModal(false);
              }}
            >
              Cancel
            </button>
            <button
              className="btnSave"
              onClick={handleUpdateUserDetail}
              disabled={updating ? true : false}
            >
              {updating ? "Saving.." : "Save"}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        closable={false}
        open={newUser.modal}
        onCancel={resetNewUserForm}
        onOk={() => {}}
        centered
        footer={null}
      >
        <div className="userDetailModalContainer">
          <div className="header">Add New User</div>
          <div className="userInfoModalContainer">
            <div className="inputHolder">
              <span className="label">Name</span>
              <input
                type="text"
                id="new_name"
                value={newUser.form.new_name}
                onChange={(e) => {
                  setNewUser({
                    ...newUser,
                    form: {
                      ...newUser.form,
                      new_name: e.target.value,
                    },
                  });
                  monitorInputChange("new_name");
                }}
                placeholder="Name & Surname"
              />
            </div>
            <div className="inputHolder">
              <span className="label">Email</span>
              <input
                type="email"
                id="new_email"
                value={newUser.form.new_email}
                onChange={(e) => {
                  setNewUser({
                    ...newUser,
                    form: {
                      ...newUser.form,
                      new_email: e.target.value,
                    },
                  });
                  monitorInputChange("new_email");
                }}
                placeholder="Email"
              />
            </div>
            <div className="inputHolder">
              <span className="label">Password</span>
              <div className="passwordHolder" id="new_password">
                <input
                  type={showPassword ? "text" : "password"}
                  value={newUser.form.new_password}
                  onChange={(e) => {
                    setNewUser({
                      ...newUser,
                      form: {
                        ...newUser.form,
                        new_password: e.target.value,
                      },
                    });
                    monitorInputChange("new_password");
                  }}
                  placeholder="Password (atleast 6 characters)"
                />
                <div className="toggleIcon">
                  <span
                    onClick={() => {
                      setShowPassword((prev) => !prev);
                    }}
                  >
                    {showPassword ? visibleIcon : invisibleIcon}
                  </span>
                </div>
              </div>
            </div>
            <div className="inputHolder">
              <span className="label">Organization</span>
              <input
                type="text"
                id="new_organization"
                value={newUser.form.new_organization}
                onChange={(e) => {
                  setNewUser({
                    ...newUser,
                    form: {
                      ...newUser.form,
                      new_organization: e.target.value,
                    },
                  });
                  monitorInputChange("new_organization");
                }}
                placeholder="Organization"
              />
            </div>
            <div className="buttonHolder">
              <button onClick={resetNewUserForm}>Cancel</button>
              <button className="btnSave" onClick={handleCreateNewUser}>
                Create
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Users;
